import React, { useEffect, useMemo, useState } from "react";
import { useData } from "../../../context/DataContext.jsx";
import { C, font, mono, btnP, btnS, btnD, inp } from "../../../lib/styles.js";
import { fmtMoney, fmtDate, FMT_DATE_FULL } from "../../../lib/utils.js";
import {
  applyVersionToQuote,
  computeQuoteRestoreImpact,
  isLegacyVersion,
  isMarkerVersion,
  summarizeVersion,
  eventDay,
} from "../../../lib/quoteVersions.js";

export default function QuoteVersionHistoryModal({ quote, linkedJob, onClose, onOpenBuilder }) {
  const { restoreQuoteVersion, forceSyncJobRevenueFromQuote, quoteBusy = null } = useData();
  // A restore that passed its time limit says so here, above the builder's own message.
  const stuck = quoteBusy?.stuck && quoteBusy.quoteId === quote?.id ? quoteBusy.message : "";
  const versions = useMemo(() => [...(quote?.versions || [])].reverse(), [quote?.versions]);
  const [selectedKey, setSelectedKey] = useState("");
  const [confirmInvoiced, setConfirmInvoiced] = useState(false);
  const [paidConfirm, setPaidConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [forceOffer, setForceOffer] = useState(null);
  const [forceAck, setForceAck] = useState(false);

  useEffect(() => {
    setSelectedKey("");
    setConfirmInvoiced(false);
    setPaidConfirm("");
    setMessage("");
    setForceOffer(null);
    setForceAck(false);
  }, [quote?.id]);

  if (!quote) return null;

  const selected = versions.find((v, i) => versionKey(v, i) === selectedKey) || null;
  const selectedSummary = selected ? summarizeVersion(selected) : null;
  const pending = selected && !isLegacyVersion(selected) && !isMarkerVersion(selected) ? applyVersionToQuote(quote, selected) : null;
  const impact = selected ? computeQuoteRestoreImpact({
    currentQuote: quote,
    targetVersion: selected,
    linkedJob,
    recomputedTotal: pending?.total,
  }) : null;
  const invoiceGuard = !!(linkedJob?.invoiced || linkedJob?.invoiceDate);
  const paidGuard = !!(linkedJob?.paid || linkedJob?.paidDate);
  const blocked = !!impact?.blockers?.length;
  const readyForGuard = paidGuard ? paidConfirm.trim().toUpperCase() === "RESTORE" : (!invoiceGuard || confirmInvoiced);
  const canRestore = selected && !blocked && readyForGuard && !busy;

  const handleRestore = async () => {
    if (!canRestore) return;
    setBusy(true);
    setMessage("");
    setForceOffer(null);
    setForceAck(false);
    try {
      const result = await restoreQuoteVersion(quote.id, selected.id);
      if (result?.cancelled) {
        // Cancel goes back to the quote: the builder with the edits the dialog kept.
        setMessage("Nothing was restored. The quote is still open in the builder with its unsaved edits.");
        if (onOpenBuilder) { onClose?.(); onOpenBuilder(); }
      } else if (result?.revenueSyncSkipped) {
        setForceOffer({
          from: Number(result.revenueSync?.linkedJob?.grossRevenue ?? linkedJob?.grossRevenue ?? 0),
          total: result.restoredTotal,
        });
        setMessage("Quote restored. Job is invoiced/paid - gross revenue was not updated.");
      } else {
        setMessage("Quote restored.");
      }
    } catch (e) {
      setMessage(e?.userMessage || e?.message || "Could not restore quote.");
    } finally {
      setBusy(false);
    }
  };

  const handleForce = async () => {
    if (!forceOffer || !forceAck || busy) return;
    setBusy(true);
    try {
      const result = await forceSyncJobRevenueFromQuote(quote.id, forceOffer.total);
      // JM16: a FAILED sync is not "already matched" — keep the offer on screen
      // so the operator can retry, and only clear it on a real outcome.
      if (result?.synced) {
        setMessage("Job gross revenue updated.");
        setForceOffer(null);
      } else if (result?.reason === "unchanged") {
        setMessage("Job gross revenue already matched.");
        setForceOffer(null);
      } else {
        setMessage("Could not update job revenue. Try again.");
      }
    } catch (e) {
      setMessage(e.message || "Could not update job revenue.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={S.backdrop} role="dialog" aria-modal="true" onMouseDown={onClose}>
      <div style={S.modal} onMouseDown={e => e.stopPropagation()}>
        <div style={S.head}>
          <div>
            <div style={S.kicker}>Quote Versions</div>
            <div style={S.title}>{quote.clientName || quote.id}</div>
          </div>
          <button type="button" style={S.close} onClick={onClose} aria-label="Close">x</button>
        </div>

        <div style={S.body}>
          <div style={S.list}>
            {versions.length === 0 ? (
              <div style={S.empty}>No quote versions yet.</div>
            ) : versions.map((version, idx) => {
              const summary = summarizeVersion(version);
              const key = versionKey(version, idx);
              const active = selectedKey === key;
              const isMarker = summary.isMarker;
              return (
                <button
                  type="button"
                  key={key}
                  style={{
                    ...S.versionBtn,
                    ...(isMarker ? S.markerVersionBtn : null),
                    borderColor: active ? C.text : (isMarker ? `${C.amber}55` : C.border),
                    background: active ? C.surfaceAlt : (isMarker ? `${C.amber}0D` : C.surface),
                  }}
                  onClick={() => {
                    setSelectedKey(key);
                    setConfirmInvoiced(false);
                    setPaidConfirm("");
                    setMessage("");
                    setForceOffer(null);
                    setForceAck(false);
                  }}
                >
                  <span style={S.versionMain}>
                    <span style={S.versionDate}>{isMarker ? `Restore audit - ${formatVersionDate(summary.timestamp)}` : formatVersionDate(summary.timestamp)}</span>
                    <span style={isMarker ? S.markerMeta : S.versionMeta}>
                      {isMarker
                        ? (summary.note || "Quote restored from prior version")
                        : `${fmtMoney(summary.total)} | ${summary.lineItemCount} item${summary.lineItemCount === 1 ? "" : "s"} | ${summary.sessionCount} session${summary.sessionCount === 1 ? "" : "s"}${summary.blockLabels.length ? ` | ${summary.blockLabels.length} block${summary.blockLabels.length === 1 ? "" : "s"}` : ""}`}
                    </span>
                  </span>
                  <span style={isMarker ? S.markerBadge : (summary.isLegacy ? S.lockBadge : S.reviewBadge)}>
                    {isMarker ? "Audit" : (summary.isLegacy ? "View-only" : "Review restore impact")}
                  </span>
                </button>
              );
            })}
          </div>

          <div style={S.review}>
            {!selected ? (
              <div style={S.empty}>Select a version to review.</div>
            ) : (
              <>
                <div style={S.reviewHead}>
                  <div>
                    <div style={S.kicker}>{isLegacyVersion(selected) || isMarkerVersion(selected) ? "View-only" : "Impact Summary"}</div>
                    <div style={S.reviewTitle}>{selectedSummary.note || selectedSummary.source}</div>
                  </div>
                  {pending && <div style={S.totalBox}>{fmtMoney(quote.total || 0)} -&gt; {fmtMoney(pending.total || 0)}</div>}
                </div>
                {/* The version's own blocks (2026-09-04): the list a restore brings back, in print order. */}
                {!isMarkerVersion(selected) && (
                  <div data-testid="version-blocks" style={S.blocksRow}>
                    {selectedSummary.blockLabels.length
                      ? `Blocks: ${selectedSummary.blockLabels.join(", ")}`
                      : "No blocks on this version."}
                  </div>
                )}

                {impact?.blockers?.length > 0 && <ImpactBucket title="BLOCKERS" items={impact.blockers} tone="danger" />}
                {!blocked && (
                  <>
                    <ImpactBucket title="WILL CHANGE" items={impact?.willChange || []} tone="change" />
                    <ImpactBucket title="WILL NOT CHANGE" items={impact?.willNotChange || []} tone="quiet" />
                    <ImpactBucket title="NEEDS REVIEW" items={impact?.needsReview || []} tone="warn" empty="No special review flags." />
                  </>
                )}

                {invoiceGuard && !paidGuard && !blocked && (
                  <label style={S.confirmRow}>
                    <input type="checkbox" checked={confirmInvoiced} onChange={e => setConfirmInvoiced(e.target.checked)} />
                    <span>This job is invoiced. Restore quote terms without automatic job revenue sync.</span>
                  </label>
                )}

                {paidGuard && !blocked && (
                  <div style={S.typeConfirm}>
                    <label style={S.typeLabel}>Type RESTORE to continue</label>
                    <input style={inp} value={paidConfirm} onChange={e => setPaidConfirm(e.target.value)} />
                  </div>
                )}

                {forceOffer && (
                  <div style={S.forceBox}>
                    <span>Quote restored. Job is invoiced/paid - gross revenue was not updated. Update job revenue from {fmtMoney(forceOffer.from)} to {fmtMoney(forceOffer.total)}?</span>
                    <label style={S.forceAck}>
                      <input type="checkbox" checked={forceAck} onChange={e => setForceAck(e.target.checked)} />
                      <span>Update accounting revenue for this invoiced/paid job.</span>
                    </label>
                    <button
                      type="button"
                      style={{ ...btnS, color: C.amber, borderColor: `${C.amber}60`, opacity: forceAck ? 1 : 0.45 }}
                      onClick={handleForce}
                      disabled={busy || !forceAck}
                    >Update job revenue</button>
                  </div>
                )}

                {message && <div style={S.message}>{message}</div>}
                {stuck && <div style={{ ...S.message, color: C.danger }} data-testid="versions-stuck">{stuck}</div>}

                <div style={S.actions}>
                  <button type="button" style={btnS} onClick={onClose}>Close</button>
                  <button
                    type="button"
                    style={{ ...(blocked ? btnD : btnP), opacity: canRestore ? 1 : 0.45 }}
                    disabled={!canRestore}
                    onClick={handleRestore}
                  >
                    {busy ? "Restoring..." : "Restore quote only"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ImpactBucket({ title, items, tone, empty }) {
  const colors = tone === "danger" ? { fg: C.danger, bg: "rgba(239,68,68,0.06)", bd: "rgba(239,68,68,0.25)" }
    : tone === "warn" ? { fg: C.amber, bg: `${C.amber}10`, bd: `${C.amber}35` }
    : tone === "change" ? { fg: C.blue, bg: `${C.blue}0D`, bd: `${C.blue}25` }
    : { fg: C.muted, bg: C.surface, bd: C.border };
  const rows = items?.length ? items : (empty ? [empty] : []);
  if (!rows.length) return null;
  return (
    <section style={{ ...S.bucket, borderColor: colors.bd, background: colors.bg }}>
      <div style={{ ...S.bucketTitle, color: colors.fg }}>{title}</div>
      <ul style={S.bucketList}>
        {rows.map((item, idx) => <li key={idx}>{item}</li>)}
      </ul>
    </section>
  );
}

function formatVersionDate(timestamp) {
  if (!timestamp) return "Unknown date";
  // Local calendar day (Codex 2026-09-13): the UTC slice put a late-evening
  // New York amendment on tomorrow's date next to a local clock time.
  const day = eventDay(timestamp);
  const date = fmtDate(day, FMT_DATE_FULL) || day;
  const time = timestamp.includes("T")
    ? new Date(timestamp).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })
    : "";
  return [date, time].filter(Boolean).join(" ");
}

function versionKey(version, idx) {
  return version?.id || `${version?.timestamp || "legacy"}_${idx}`;
}

const S = {
  backdrop: {
    position: "fixed", inset: 0, zIndex: 10000,
    background: "rgba(26,26,26,0.32)",
    display: "flex", alignItems: "center", justifyContent: "center",
    padding: 18,
  },
  modal: {
    width: "min(980px, 100%)",
    maxHeight: "90vh",
    overflow: "hidden",
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    boxShadow: "0 18px 50px rgba(0,0,0,0.18)",
    fontFamily: font,
    display: "flex",
    flexDirection: "column",
  },
  head: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "16px 18px",
    borderBottom: `1px solid ${C.border}`,
  },
  kicker: {
    fontSize: 10,
    textTransform: "uppercase",
    letterSpacing: "0.12em",
    color: C.muted,
    marginBottom: 4,
  },
  title: { fontSize: 18, fontWeight: 500, color: C.text },
  close: {
    width: 30, height: 30,
    borderRadius: 6,
    border: `1px solid ${C.border}`,
    background: "transparent",
    color: C.muted,
    cursor: "pointer",
    fontSize: 20,
    lineHeight: 1,
  },
  body: {
    display: "grid",
    gridTemplateColumns: "minmax(260px, 340px) 1fr",
    minHeight: 460,
    overflow: "hidden",
  },
  list: {
    borderRight: `1px solid ${C.border}`,
    padding: 12,
    overflowY: "auto",
    background: C.surfaceAlt,
  },
  versionBtn: {
    width: "100%",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 10,
    textAlign: "left",
    padding: 11,
    marginBottom: 8,
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    cursor: "pointer",
    fontFamily: font,
  },
  versionMain: { display: "grid", gap: 4, minWidth: 0 },
  versionDate: { fontSize: 12, color: C.text },
  versionMeta: { fontSize: 10.5, color: C.muted, fontFamily: mono },
  markerVersionBtn: {
    cursor: "default",
  },
  markerMeta: { fontSize: 10.5, color: C.amber, fontFamily: mono },
  lockBadge: {
    flexShrink: 0,
    fontSize: 9,
    color: C.muted,
    border: `1px solid ${C.border}`,
    borderRadius: 4,
    padding: "2px 5px",
    textTransform: "uppercase",
  },
  markerBadge: {
    flexShrink: 0,
    fontSize: 9,
    color: C.amber,
    border: `1px solid ${C.amber}45`,
    background: `${C.amber}10`,
    borderRadius: 4,
    padding: "2px 5px",
    textTransform: "uppercase",
  },
  reviewBadge: {
    flexShrink: 0,
    fontSize: 9,
    color: C.blue,
    border: `1px solid ${C.blue}35`,
    background: `${C.blue}0D`,
    borderRadius: 4,
    padding: "2px 5px",
    textTransform: "uppercase",
  },
  review: {
    padding: 16,
    overflowY: "auto",
  },
  reviewHead: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 12,
    marginBottom: 12,
  },
  reviewTitle: { fontSize: 14, color: C.text, fontWeight: 500 },
  totalBox: {
    fontFamily: mono,
    fontSize: 12,
    padding: "6px 8px",
    border: `1px solid ${C.border}`,
    borderRadius: 6,
    color: C.text,
    whiteSpace: "nowrap",
  },
  blocksRow: {
    fontFamily: mono,
    fontSize: 10.5,
    color: C.muted,
    marginBottom: 12,
  },
  bucket: {
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    padding: "10px 12px",
    marginBottom: 10,
  },
  bucketTitle: {
    fontSize: 10,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    fontWeight: 600,
    marginBottom: 7,
  },
  bucketList: {
    margin: 0,
    paddingLeft: 18,
    color: C.text,
    fontSize: 12,
    lineHeight: 1.5,
  },
  confirmRow: {
    display: "flex",
    gap: 8,
    alignItems: "flex-start",
    fontSize: 12,
    color: C.text,
    margin: "12px 0",
  },
  typeConfirm: { margin: "12px 0" },
  typeLabel: { display: "block", fontSize: 11, color: C.muted, marginBottom: 5 },
  forceBox: {
    display: "flex",
    gap: 10,
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    marginTop: 12,
    padding: 10,
    border: `1px solid ${C.amber}40`,
    borderRadius: 8,
    background: `${C.amber}10`,
    color: C.amber,
    fontSize: 12,
  },
  forceAck: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    color: C.text,
    fontSize: 11.5,
  },
  message: {
    marginTop: 10,
    fontSize: 12,
    color: C.muted,
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 8,
    marginTop: 16,
  },
  empty: {
    padding: 16,
    color: C.muted,
    fontSize: 12,
  },
};
